import React from "react";
import {
  Box,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Article as TranscriptIcon,
  Psychology as AiIcon,
} from "@mui/icons-material";
import { hapticSelection } from "../../../../lib/haptics";

export type TranscriptMode = 'transcript' | 'ai-summary';

interface ModeConfig {
  id: TranscriptMode;
  label: string;
  shortLabel: string;
  icon: React.ReactNode;
}

interface TranscriptModeToggleProps {
  mode: TranscriptMode;
  onModeChange: (mode: TranscriptMode) => void;
  disabled?: boolean;
  compact?: boolean;
}

const TranscriptModeToggle: React.FC<TranscriptModeToggleProps> = ({
  mode,
  onModeChange,
  disabled = false,
  compact = false,
}) => {
  const theme = useTheme();

  const modes: ModeConfig[] = [
    {
      id: 'ai-summary',
      label: 'AI Summary',
      shortLabel: 'AI',
      icon: <AiIcon />,
    },
    {
      id: 'transcript',
      label: 'Full Transcript',
      shortLabel: 'Full',
      icon: <TranscriptIcon />,
    },
  ];

  return (
    <Box
      sx={{
        display: "inline-flex",
        background: theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.05)"
          : "rgba(0, 0, 0, 0.04)",
        borderRadius: 2,
        padding: 0.125,
        border: theme.palette.mode === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.08)",
        position: "relative",
        overflow: "hidden",
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
        height: compact ? "28px" : "32px",
      }}
    >
      {modes.map((modeConfig) => (
        <Box
          key={modeConfig.id}
          data-tour={`transcript-mode-${modeConfig.id}`}
          onClick={() => {
            if (disabled) return;
            hapticSelection();
            onModeChange(modeConfig.id);
          }}
          sx={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            gap: compact ? 0.5 : 0.25,
            px: compact ? 0.75 : 1,
            py: compact ? 0.25 : 0.5,
            minWidth: compact ? 50 : 60,
            height: compact ? "26px" : "30px",
            cursor: disabled ? "default" : "pointer",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            borderRadius: 1.5,
            "&:hover": {},
            ...(mode === modeConfig.id && {
              background: theme.palette.mode === "dark"
                ? "#22c55e"
                : "#ffffff",
              border: `1px solid #22c55e`,
            }),
          }}
        >
          <Box
            sx={{
              fontSize: compact ? 12 : 14,
              color: mode === modeConfig.id
                ? theme.palette.mode === "dark"
                  ? "#ffffff"
                  : "#16a34a"
                : theme.palette.mode === "dark"
                  ? "#a1a1a6"
                  : "#86868b",
              transition: "color 0.3s ease",
              "& svg": {
                fontSize: "inherit",
              },
            }}
          >
            {modeConfig.icon}
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontSize: compact ? "0.625rem" : "0.6875rem",
              fontWeight: mode === modeConfig.id ? 600 : 500,
              color: mode === modeConfig.id
                ? theme.palette.mode === "dark"
                  ? "#ffffff"
                  : "#16a34a"
                : theme.palette.mode === "dark"
                  ? "#a1a1a6"
                  : "#86868b",
              transition: "all 0.3s ease",
              whiteSpace: "nowrap",
              letterSpacing: "0.25px",
              lineHeight: 1.2,
            }}
          >
            {compact ? modeConfig.shortLabel : modeConfig.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default TranscriptModeToggle;